import { useState } from "react";
import { API } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Send, Lightbulb } from "lucide-react";

const SOURCE_TYPES = [
  { value: "team_observation", label: "Team Observation" },
  { value: "external_inspiration", label: "External Inspiration" },
  { value: "video", label: "Video" },
  { value: "book", label: "Book" },
  { value: "podcast", label: "Podcast" },
  { value: "cultural_reference", label: "Cultural Reference" },
];

export default function TeamSubmit() {
  const [name, setName] = useState("");
  const [signal, setSignal] = useState("");
  const [sourceType, setSourceType] = useState("team_observation");
  const [context, setContext] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !signal.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/authority-os/team-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submitter_name: name.trim(),
          signal: signal.trim(),
          source_type: sourceType,
          context: context.trim() || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSubmitted(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSignal("");
    setContext("");
    setSubmitted(null);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md w-full text-center" data-testid="team-submit-success">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Signal Submitted</h2>
          <p className="text-sm text-slate-500 mb-1">
            Thank you, {name}. Your observation has been submitted to Rex.
          </p>
          <p className="text-xs text-slate-400 mb-6">
            ID: {submitted.authority_id} — Pending founder review.
          </p>
          <Button onClick={reset} variant="outline" data-testid="submit-another-btn">
            Submit Another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-lg w-full" data-testid="team-submit-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-800">Submit a Signal</h1>
            <p className="text-xs text-slate-400">Share observations, insights, or inspiration with Rex</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-xs text-slate-600">Your Name</Label>
            <Input
              id="name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Tunde Adeyemi"
              required
              className="mt-1"
              data-testid="submitter-name-input"
            />
          </div>

          <div>
            <Label htmlFor="source" className="text-xs text-slate-600">Signal Type</Label>
            <select
              id="source"
              value={sourceType}
              onChange={e => setSourceType(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 bg-white"
              data-testid="source-type-select"
            >
              {SOURCE_TYPES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <Label htmlFor="signal" className="text-xs text-slate-600">Your Observation or Insight</Label>
            <Textarea
              id="signal"
              value={signal}
              onChange={e => setSignal(e.target.value)}
              placeholder="e.g. Clients in Kenya are asking about succession planning more frequently this quarter..."
              rows={4}
              required
              className="mt-1"
              data-testid="signal-input"
            />
          </div>

          <div>
            <Label htmlFor="context" className="text-xs text-slate-600">Additional Context (optional)</Label>
            <Input
              id="context"
              value={context}
              onChange={e => setContext(e.target.value)}
              placeholder="e.g. Heard during client check-in with Safaricom"
              className="mt-1"
              data-testid="context-input"
            />
          </div>

          <Button type="submit" disabled={submitting || !name.trim() || !signal.trim()} className="w-full" data-testid="submit-signal-btn">
            {submitting ? "Submitting..." : <><Send className="w-4 h-4 mr-2" /> Submit Signal</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
